import { clsx } from 'clsx';
import { Modal, Input } from 'antd';
import { useEffect, useState } from 'react';
import { Button } from '@mui/material';

import { axiosErrorToString, dateFormatter } from '@utils';
import { Loader } from '@components';
import cls from './Footer.module.css';
import { useNotify } from 'react-admin';
import { dealerHttp } from '@network';
import { DealerComment } from '@types';

const { TextArea } = Input;

interface Props {
  formValues: any;
}

interface CommentProps {
  id: number;
  content: string;
  date: string;
  author: string;
}

const Comment = ({ content, author, date }: CommentProps) => {
  return (
    <div className={cls.comment}>
      <div className={cls.commentContent}>{content}</div>
      <div className={cls.commentFooter}>
        <div className={cls.commentAuthor}>{author}</div>
        <div className={cls.commentDate}>{dateFormatter.toDateTime(date)}</div>
      </div>
    </div>
  );
};

export const Footer = ({ formValues }: Props) => {
  const notify = useNotify();
  const [loading, setLoading] = useState<boolean>(false);
  const [isDialogFormOpen, setIsDialogFormOpen] = useState<boolean>(false);
  const [isDialogListOpen, setIsDialogListOpen] = useState<boolean>(false);
  const [comment, setComment] = useState('');
  const [comments, setComments] = useState<any>([]);

  const onCommentFormClear = () => {
    setComment('');
    setIsDialogFormOpen(false);
  };

  const onCommentFormSend = async () => {
    try {
      setLoading(true);
      await dealerHttp.createComment(formValues.id, comment);
      await getComments();
      onCommentFormClear();
    } catch (e) {

    } finally {
      setLoading(false);
    }
  };

  const getComments = async () => {
    if (!formValues.id) return;
    try {
      setLoading(true);
      const comments = await dealerHttp.getComments(formValues.id);
      setComments(comments);
    } catch (e) {
      notify(axiosErrorToString(e));
    } finally {
      setLoading(false);
    }
  };

  const showComments = () => {
    setIsDialogListOpen(true);
  };

  useEffect(() => {
    (async () => {
      await getComments();
    })();
  }, []);

  return (
    <div className={cls.wrap}>
      <div className={cls.info}>
        <div className={cls.salesForce}>Salesforce ID: 003Vr00000QF0btIAD</div>
        {formValues.lastUpdatedAt ? <div className={cls.lastUpdate}>
          Last Update: {
            dateFormatter
              .toDateSlash(formValues.lastUpdatedAt)
          } at {
            dateFormatter
              .toTime(formValues.lastUpdatedAt)
          }
          {formValues.lastUpdatedName ? `, @${formValues.lastUpdatedName}` : ''}
        </div> : null}
      </div>

      <div className={cls.actions}>
        <Button
          className={clsx(cls.bottomBtn, cls.show)}
          variant="contained"
          disabled={!comments.length}
          onClick={showComments}>
          View Notes
          {comments.length ? <span className={cls.counter}>{comments.length}</span> : null}
        </Button>

        <Button
          className={clsx(cls.bottomBtn, cls.add)}
          variant="contained"
          onClick={() => setIsDialogFormOpen(true)}
        >
          Add Note +
        </Button>
      </div>

      <Modal
        title={<span>Add note{loading ? <Loader mini className={cls.loader}/> : null}</span>}
        open={isDialogFormOpen}
        cancelButtonProps={{ className: cls.cancelBtn }}
        okButtonProps={{ className: cls.okBtn }}
        onOk={!loading ? onCommentFormSend : undefined}
        okText="Save"
        okType="primary"
        onCancel={onCommentFormClear}>
        <TextArea
          value={comment}
          onChange={e => setComment(e.target.value)}
          autoSize={{ minRows: 5, maxRows: 15 }}
        />
      </Modal>

      <Modal
        title="Notes"
        wrapClassName={cls.listNotes}
        centered
        okButtonProps={{ hidden: true }}
        cancelButtonProps={{ hidden: true }}
        footer={null}
        open={isDialogListOpen}
        onCancel={() => setIsDialogListOpen(false)}>
        <div className={cls.listNotesWrap}>
          {comments.map((i: DealerComment) => <Comment
            key={i.id}
            id={i.id}
            author={i.adminName}
            date={i.createdAt}
            content={i.comment}
          />)}
        </div>
      </Modal>
    </div>
  );
};
